// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { MedplumClient } from '@medplum/core';
import { createReference } from '@medplum/core';
import type { Consent, Patient } from '@medplum/fhirtypes';
import {
  CONSENT_DEFINITIONS,
  FEATURED_CONSENT_KEY,
  findConsentForDefinition,
  getConsentChannels,
  isConsentActive,
  SMS_CONSENT_KEY,
  withChannelsOnConsent,
} from '@polarissleep/shared';
import { withTenantScope } from '../../utils/tenant';

const PREFERRED_COMMUNICATION_METHOD_EXTENSION_URL =
  'http://polarissleep.com/fhir/StructureDefinition/preferred-communication-method';
const CONTACT_CHANNEL_PHONE = 'phone';
const CONTACT_CHANNEL_EMAIL = 'email';
const CONTACT_CHANNEL_SMS = 'sms';

function getPatientPreferredContactMethods(patient: Patient): Set<string> {
  return new Set(
    (patient.extension ?? [])
      .filter((extension) => extension.url === PREFERRED_COMMUNICATION_METHOD_EXTENSION_URL)
      .map((extension) => extension.valueCoding?.code ?? extension.valueString)
      .filter((code): code is string => typeof code === 'string' && code.length > 0)
  );
}

function getPatientPreferredContactChannels(patient: Patient): Set<string> {
  const methods = getPatientPreferredContactMethods(patient);
  return new Set([CONTACT_CHANNEL_PHONE, CONTACT_CHANNEL_EMAIL].filter((channel) => methods.has(channel)));
}

function hasPatientPreferredSms(patient: Patient): boolean {
  return getPatientPreferredContactMethods(patient).has(CONTACT_CHANNEL_SMS);
}

export async function syncPreferredContactMethodsToConsents(
  medplum: MedplumClient,
  patient: Patient,
  consents: Consent[]
): Promise<Consent[]> {
  if (!patient.id) {
    return consents;
  }

  const contactDef = CONSENT_DEFINITIONS.find((definition) => definition.key === FEATURED_CONSENT_KEY);
  const smsDef = CONSENT_DEFINITIONS.find((definition) => definition.key === SMS_CONSENT_KEY);
  if (!contactDef || !smsDef) {
    return consents;
  }

  let working = consents;
  const contactConsent = findConsentForDefinition(working, contactDef);
  const preferredChannels = getPatientPreferredContactChannels(patient);

  if (
    preferredChannels.size > 0 &&
    contactConsent?.id &&
    isConsentActive(contactConsent) &&
    getConsentChannels(contactConsent).size === 0
  ) {
    const saved = await medplum.updateResource(withChannelsOnConsent(contactConsent, preferredChannels));
    working = working.map((consent) => (consent.id === contactConsent.id ? saved : consent));
  }

  const currentSmsConsent = findConsentForDefinition(working, smsDef);
  if (
    hasPatientPreferredSms(patient) &&
    isConsentActive(findConsentForDefinition(working, contactDef)) &&
    !currentSmsConsent
  ) {
    const saved = await medplum.createResource(
      withTenantScope({
        resourceType: 'Consent',
        patient: createReference(patient),
        status: 'active',
        scope: smsDef.scope,
        category: [smsDef.category],
        policyRule: smsDef.policyRule,
        dateTime: new Date().toISOString(),
      })
    );
    working = [...working, saved];
  }

  return working;
}
